import type { QueryClient } from "@tanstack/react-query";

import {
  getSmartTableVibeRefreshScope,
  normalizeSmartTableToolName,
  shouldPublishSmartTableVibeGridRefresh,
  SmartTableToolName,
  type SmartTableVibeRefreshScope,
} from "./smartTableToolNames";

export {
  getSmartTableVibeRefreshScope,
  shouldPublishSmartTableVibeGridRefresh,
  SmartTableToolName,
  type SmartTableVibeRefreshScope,
} from "./smartTableToolNames";

export interface VibeChatToolResultPayload {
  toolName?: string;
  toolInput?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

export interface SmartTableVibeRefreshDetail {
  workspaceId: number;
  tableId?: string;
  sheetId?: string;
  toolName: string;
  scope: SmartTableVibeRefreshScope;
  clearColumns: boolean;
  knownRowCount?: number;
}

type SmartTableVibeRefreshListener = (detail: SmartTableVibeRefreshDetail) => void;

const smartTableVibeRefreshListeners = new Set<SmartTableVibeRefreshListener>();

export const subscribeSmartTableVibeRefresh = (
  listener: SmartTableVibeRefreshListener,
): (() => void) => {
  smartTableVibeRefreshListeners.add(listener);
  return () => {
    smartTableVibeRefreshListeners.delete(listener);
  };
};

export const shouldClearColumnsOnSmartTableVibeRefresh = (toolName: string): boolean =>
  getSmartTableVibeRefreshScope(toolName) === "sheetColumns";

const publishSmartTableVibeRefresh = (detail: SmartTableVibeRefreshDetail): void => {
  smartTableVibeRefreshListeners.forEach(listener => listener(detail));
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asStringId = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const isSmartTableVibeToolName = (toolName: string): boolean =>
  normalizeSmartTableToolName(toolName).includes("smart_table");

export const shouldInvalidateSmartTableQueriesForTool = (toolName: string): boolean => {
  if (!isSmartTableVibeToolName(toolName)) {
    return false;
  }

  switch (normalizeSmartTableToolName(toolName)) {
    case SmartTableToolName.RUN_SMART_TABLE:
    case SmartTableToolName.GET_SMART_TABLE_GRID:
    case SmartTableToolName.LIST_SMART_TABLE_SHEETS:
      return false;
    default:
      return true;
  }
};

export const extractSmartTableIdsFromToolPayload = (
  toolInput?: Record<string, unknown>,
  result?: Record<string, unknown>,
): { tableId?: string; sheetId?: string } => {
  const smartTable = asRecord(result?.smart_table);
  const sheet = asRecord(result?.sheet);
  const column = asRecord(result?.column);
  const cell = asRecord(result?.cell);

  return {
    tableId:
      asStringId(toolInput?.smart_table_id) ??
      asStringId(smartTable?.id) ??
      asStringId(sheet?.smart_table_id) ??
      asStringId(column?.smart_table_id) ??
      asStringId(cell?.smart_table_id),
    sheetId:
      asStringId(toolInput?.sheet_id) ??
      asStringId(sheet?.id) ??
      asStringId(column?.sheet_id) ??
      asStringId(cell?.sheet_id),
  };
};

export const extractKnownRowCountFromToolResult = (
  result?: Record<string, unknown>,
): number | undefined => {
  const sheet = asRecord(result?.sheet);
  const sheetRowCount = sheet?.row_count;
  if (typeof sheetRowCount === "number" && Number.isFinite(sheetRowCount)) {
    return Math.max(0, Math.trunc(sheetRowCount));
  }

  const sheetId = asStringId(sheet?.id);
  const smartTable = asRecord(result?.smart_table);
  const sheetRowCounts = asRecord(smartTable?.sheet_row_counts);
  if (sheetId && sheetRowCounts) {
    const count = sheetRowCounts[sheetId];
    if (typeof count === "number" && Number.isFinite(count)) {
      return Math.max(0, Math.trunc(count));
    }
  }

  return undefined;
};

const invalidateWorkspaceTableList = (queryClient: QueryClient, workspaceId: number): void => {
  queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, "list"] });
};

const invalidateTableSheetStructure = (
  queryClient: QueryClient,
  workspaceId: number,
  tableId: string,
): void => {
  queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, tableId, "sheets"] });
  queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, tableId, "bootstrap"] });
};

const invalidateSheetColumnGridVersions = (
  queryClient: QueryClient,
  workspaceId: number,
  tableId: string,
  sheetId: string,
): void => {
  queryClient.invalidateQueries({
    queryKey: ["smart-tables", workspaceId, tableId, sheetId, "columns"],
  });
  queryClient.invalidateQueries({
    queryKey: ["smart-tables", workspaceId, tableId, sheetId, "grid"],
  });
  queryClient.invalidateQueries({
    queryKey: ["smart-tables", workspaceId, tableId, sheetId, "versions"],
  });
};

const invalidateSheetGridVersions = (
  queryClient: QueryClient,
  workspaceId: number,
  tableId: string,
  sheetId: string,
): void => {
  queryClient.invalidateQueries({
    queryKey: ["smart-tables", workspaceId, tableId, sheetId, "grid"],
  });
  queryClient.invalidateQueries({
    queryKey: ["smart-tables", workspaceId, tableId, sheetId, "versions"],
  });
};

const isSmartTableSheetQueryKey = (
  queryKey: readonly unknown[],
  workspaceId: number,
  sheetId: string,
): boolean =>
  queryKey.length >= 4 &&
  queryKey[0] === "smart-tables" &&
  queryKey[1] === workspaceId &&
  queryKey.includes(sheetId);

const invalidateSheetColumnGridVersionsBySheetId = (
  queryClient: QueryClient,
  workspaceId: number,
  sheetId: string,
): void => {
  queryClient.invalidateQueries({
    predicate: query => isSmartTableSheetQueryKey(query.queryKey, workspaceId, sheetId),
  });
};

interface PublishSmartTableVibeRefreshInput {
  workspaceId: number;
  tableId?: string;
  sheetId?: string;
  toolName: string;
  result?: Record<string, unknown>;
}

const tryPublishSmartTableVibeRefresh = ({
  workspaceId,
  tableId,
  sheetId,
  toolName,
  result,
}: PublishSmartTableVibeRefreshInput): void => {
  const scope = getSmartTableVibeRefreshScope(toolName);
  if (!shouldPublishSmartTableVibeGridRefresh(scope, sheetId) || !scope || !sheetId) {
    return;
  }

  publishSmartTableVibeRefresh({
    workspaceId,
    tableId,
    sheetId,
    toolName,
    scope,
    clearColumns: scope === "sheetColumns",
    knownRowCount: extractKnownRowCountFromToolResult(result),
  });
};

export const invalidateSmartTableQueriesFromVibeToolCall = (
  queryClient: QueryClient,
  workspaceId: number,
  payload: VibeChatToolResultPayload,
): void => {
  const toolName = payload.toolName?.trim();
  if (!toolName || !shouldInvalidateSmartTableQueriesForTool(toolName)) {
    return;
  }

  const normalized = normalizeSmartTableToolName(toolName);
  const { tableId, sheetId } = extractSmartTableIdsFromToolPayload(
    payload.toolInput,
    payload.result,
  );

  switch (normalized) {
    case SmartTableToolName.CREATE_SMART_TABLE:
    case SmartTableToolName.DELETE_SMART_TABLE:
      invalidateWorkspaceTableList(queryClient, workspaceId);
      queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
      break;
    case SmartTableToolName.UPDATE_SMART_TABLE:
      invalidateWorkspaceTableList(queryClient, workspaceId);
      break;
    default:
      break;
  }

  if (!tableId) {
    if (!sheetId) {
      return;
    }

    switch (normalized) {
      case SmartTableToolName.CREATE_SMART_TABLE_COLUMN:
      case SmartTableToolName.UPDATE_SMART_TABLE_COLUMN:
      case SmartTableToolName.DELETE_SMART_TABLE_COLUMN:
        invalidateSheetColumnGridVersionsBySheetId(queryClient, workspaceId, sheetId);
        break;
      case SmartTableToolName.ADD_SMART_TABLE_ROWS:
      case SmartTableToolName.DELETE_SMART_TABLE_ROWS:
      case SmartTableToolName.UPDATE_SMART_TABLE_CELL:
      case SmartTableToolName.IMPORT_SMART_TABLE_REQUEST_HISTORY:
        queryClient.invalidateQueries({
          predicate: query => isSmartTableSheetQueryKey(query.queryKey, workspaceId, sheetId),
        });
        break;
      default:
        break;
    }

    tryPublishSmartTableVibeRefresh({
      workspaceId,
      tableId,
      sheetId,
      toolName,
      result: payload.result,
    });
    return;
  }

  queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, tableId] });

  switch (normalized) {
    case SmartTableToolName.CREATE_SMART_TABLE:
    case SmartTableToolName.CREATE_SMART_TABLE_SHEET:
    case SmartTableToolName.UPDATE_SMART_TABLE_SHEET:
    case SmartTableToolName.DELETE_SMART_TABLE_SHEET:
      invalidateTableSheetStructure(queryClient, workspaceId, tableId);
      break;
    default:
      break;
  }

  if (normalized === SmartTableToolName.CREATE_SMART_TABLE && sheetId) {
    queryClient.invalidateQueries({
      queryKey: ["smart-tables", workspaceId, tableId, sheetId],
    });
    queryClient.invalidateQueries({
      queryKey: ["smart-tables", workspaceId, tableId, sheetId, "grid"],
    });
    queryClient.invalidateQueries({
      queryKey: ["smart-tables", workspaceId, tableId, sheetId, "columns"],
    });
  }

  if (!sheetId) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, tableId, sheetId] });

  switch (normalized) {
    case SmartTableToolName.CREATE_SMART_TABLE_COLUMN:
    case SmartTableToolName.UPDATE_SMART_TABLE_COLUMN:
    case SmartTableToolName.DELETE_SMART_TABLE_COLUMN:
      invalidateSheetColumnGridVersions(queryClient, workspaceId, tableId, sheetId);
      break;
    case SmartTableToolName.ADD_SMART_TABLE_ROWS:
    case SmartTableToolName.DELETE_SMART_TABLE_ROWS:
    case SmartTableToolName.UPDATE_SMART_TABLE_CELL:
    case SmartTableToolName.IMPORT_SMART_TABLE_REQUEST_HISTORY:
      invalidateSheetGridVersions(queryClient, workspaceId, tableId, sheetId);
      break;
    case SmartTableToolName.CONFIGURE_SMART_TABLE_SCORE:
    case SmartTableToolName.CONFIGURE_SMART_TABLE_SCORECARD:
    case SmartTableToolName.RECALCULATE_SMART_TABLE_SCORECARD:
      queryClient.invalidateQueries({
        queryKey: ["smart-tables", workspaceId, tableId, sheetId, "score"],
      });
      queryClient.invalidateQueries({
        queryKey: ["smart-table-scorecard", workspaceId, tableId, sheetId],
      });
      queryClient.invalidateQueries({
        queryKey: ["smart-table-scorecard-rows", workspaceId, tableId, sheetId],
      });
      queryClient.invalidateQueries({
        queryKey: ["smart-tables", workspaceId, tableId, sheetId, "versions"],
      });
      break;
    case SmartTableToolName.GET_SMART_TABLE_SCORECARD:
      queryClient.invalidateQueries({
        queryKey: ["smart-table-scorecard", workspaceId, tableId, sheetId],
      });
      break;
    default:
      break;
  }

  tryPublishSmartTableVibeRefresh({
    workspaceId,
    tableId,
    sheetId,
    toolName,
    result: payload.result,
  });
};

export const handleVibeChatToolResultInvalidations = (
  queryClient: QueryClient,
  workspaceId: number,
  payload: VibeChatToolResultPayload,
): void => {
  queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
  invalidateSmartTableQueriesFromVibeToolCall(queryClient, workspaceId, payload);
};
